// eslint-disable-next-line
import React, { ChangeEvent, KeyboardEvent, useEffect, useState, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import SearchBox from 'components/common/SearchBox';
import Avatar from 'components/base/Avatar';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Badge from 'components/base/Badge';
import FilterTab from 'components/common/FilterTab';
import * as noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import { fetchHVASearchCalls } from 'services/hvasearchcall.service';
import { ColumnDef, Row } from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import HVASpinner from 'components/base/HVASpinner';

interface Call {
  servicename: string;
  servicebrand: string;
  campaignname: string;
  campaignid: number;
  callid: string;
  agent: string;
  accountref: string;
  customerref: string;
  customerfullname: string;
  phonenumber: string;
  calldatetime: string;
  duration: string;
  audiosource: string;
  calltype: string;
}

type CallListProps = {
  service?: string;
  campaign?: string;
  hva?: string;
  firstName?: string;
  lastName?: string;
  accountID?: string;
  customerID?: string;
  phoneNumber?: string;
  startDate?: string;
  endDate?: string;
  selectedCallId?: string | null;
  onCallIdSelect?: (
    callId: string,
    campaignId: number,
    customer: string,
    dateYphone: string,
    audiosource: string
  ) => void; // Prop para la función de callback
};

const CallList: React.FC<CallListProps> = ({
  service,
  campaign,
  hva,
  firstName,
  lastName,
  accountID,
  customerID,
  phoneNumber,
  startDate,
  endDate,
  selectedCallId,
  onCallIdSelect // Recibimos la función de callback
}) => {
  const [data, setData] = useState<Call[]>([]);
  const [maxDuration, setMaxDuration] = useState(300);
  // eslint-disable-next-line
  const [durationRange, setDurationRange] = useState<[number, number]>([0, 300]);
  const [isLoading, setIsLoading] = useState(true); // Estado para el spinner
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderInstanceRef = useRef<noUiSlider.API | null>(null);
  const [minDurationInput, setMinDurationInput] = useState<string>(
    durationRange[0].toString()
  );
  const [maxDurationInput, setMaxDurationInput] = useState<string>(
    durationRange[1].toString()
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!startDate || !endDate) {
          setData([]);
          setIsLoading(false);
          return;
        }

        const calls = await fetchHVASearchCalls({
          service,
          campaign,
          hva,
          firstName,
          lastName,
          accountID,
          customerID,
          phoneNumber,
          startDate,
          endDate
        });

        const transformedCalls = calls.map((call: Call) => ({
          ...call,
          calldatetime: dayjs(call.calldatetime).format('DD-MM-YYYY HH:mm:ss'),
          duration: call.duration.toString()
        }));

        setData(transformedCalls);

        if (calls.length === 0) {
          // Si no hay registros, establecer el máximo en 300
          setMaxDuration(300);
        } else {
          // Calcular la duración máxima
          const maxDurationValue = calls.reduce((max: number, call: Call) => {
            const duration = parseInt(call.duration, 10);
            return duration > max ? duration : max;
          }, 0);
          setMaxDuration(maxDurationValue);

          // Actualizar el rango de duración con el nuevo valor máximo
          setDurationRange([0, maxDurationValue]);
        }
      } catch (error) {
        alert('Error fetching calls:' + error);
      } finally {
        setIsLoading(false); // Desactivar el spinner una vez que se carguen los datos
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    service,
    campaign,
    hva,
    firstName,
    lastName,
    accountID,
    customerID,
    phoneNumber
  ]);

  useEffect(() => {
    if (sliderRef.current && !sliderInstanceRef.current) {
      sliderInstanceRef.current = noUiSlider.create(sliderRef.current, {
        start: durationRange,
        connect: true,
        range: {
          min: 0,
          max: maxDuration || 300
        },
        step: 1
      });

      sliderInstanceRef.current.on('update', (values: (string | number)[]) => {
        const newMin = parseInt(values[0] as string, 10);
        const newMax = parseInt(values[1] as string, 10);

        setDurationRange([newMin, newMax]);
        setMinDurationInput(newMin.toString());
        setMaxDurationInput(newMax.toString());
      });
    }

    // Limpiar el slider solo cuando el componente se desmonte
    return () => {
      if (sliderInstanceRef.current) {
        sliderInstanceRef.current.destroy();
        sliderInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.updateOptions(
        {
          range: {
            min: 0,
            max: maxDuration
          }
        },
        true
      );
      sliderInstanceRef.current.set([0, maxDuration]);
      setMinDurationInput('0');
      setMaxDurationInput(maxDuration.toString());
    }
  }, [maxDuration]);

  const filteredData = useMemo(() => {
    return data.filter(call => {
      const duration = parseInt(call.duration, 10);
      return duration >= durationRange[0] && duration <= durationRange[1];
    });
  }, [data, durationRange]);

  const columns: ColumnDef<Call>[] = [
    {
      accessorKey: 'campaignname',
      header: 'Campaign',
      cell: ({ row: { original } }) => {
        const { campaignname, servicebrand } = original;
        const [imageSrc, setImageSrc] = useState(`/brands/${servicebrand}.png`);

        const handleImageError = () => {
          setImageSrc('/brands/default.png');
        };

        return (
          <div className="d-flex align-items-center">
            {imageSrc && (
              <Avatar
                src={imageSrc}
                size="s"
                alt={campaignname}
                onError={handleImageError}
              />
            )}
            &nbsp;{campaignname}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '12%' }, className: 'pe-2 ps-0 fs-10' },
        cellProps: { className: 'p-0 text-body white-space-nowrap' }
      }
    },
    {
      accessorKey: 'callid',
      header: 'Call ID',
      cell: ({ row: { original } }) => {
        // Ya no necesitas el handleClick aquí
        return <span>{original.callid}</span>;
      },
      meta: {
        headerProps: { style: { width: '12%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'text-body px-1' }
      }
    },
    {
      accessorKey: 'agent',
      header: 'HVA',
      meta: {
        headerProps: { style: { width: '5%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'text-body px-1' }
      }
    },
    {
      accessorKey: 'accountref',
      header: 'Account ID',
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'text-body px-1' }
      }
    },
    {
      accessorKey: 'customerref',
      header: 'Customer ID',
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'text-body px-1' }
      }
    },
    {
      accessorKey: 'customerfullname',
      header: 'Customer Name',
      meta: {
        headerProps: { style: { width: '12%' }, className: 'px-1 fs-10' },
        cellProps: {
          className: 'text-body px-1 white-space-nowrap'
        }
      }
    },
    {
      accessorKey: 'phonenumber',
      header: 'Phone #',
      meta: {
        headerProps: { style: { width: '8%' }, className: 'px-1 fs-10' },
        cellProps: {
          className: 'text-body px-1 white-space-nowrap'
        }
      }
    },
    {
      accessorKey: 'calldatetime',
      header: 'Call date & time',
      cell: ({ row: { original } }) => {
        // Parse the date string in the format dd-mm-yyyy hh:mm:ss
        const parsedDate = parse(
          original.calldatetime,
          'dd-MM-yyyy HH:mm:ss',
          new Date()
        );
        const formattedDate = format(parsedDate, 'MMM dd, yyyy - HH:mm:ss');
        return <span>{formattedDate}</span>;
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'text-body px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'duration',
      header: 'Duration (sec.)',
      meta: {
        headerProps: { style: { width: '9%' }, className: 'px-1 fs-10' },
        cellProps: {
          className: 'text-body d-flex justify-content-center'
        }
      }
    },
    {
      accessorKey: 'calltype',
      header: 'Call Type',
      cell: ({ row: { original } }) => {
        const getBadgeColor = (type: string) => {
          // Define los colores según los tipos de llamada que tengas
          switch (type.toLowerCase()) {
            case 'inbound':
              return 'info';
            case 'outbound':
              return 'success';
            default:
              return 'secondary';
          }
        };

        return (
          <Badge variant="phoenix" bg={getBadgeColor(original.calltype)}>
            {original.calltype}
          </Badge>
        );
      },
      filterFn: (row: Row<Call>, columnId: string, filterValue: string) => {
        if (!filterValue || filterValue === 'all') return true;
        return row.getValue(columnId) === filterValue;
      },
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'px-1 fs-10 text-center'
        },
        cellProps: {
          className: 'text-body-tertiary px-3 text-center'
        }
      }
    }
  ];

  const table = useAdvanceTable({
    data: filteredData,
    columns: columns,
    pageSize: 8,
    pagination: true,
    sortable: true,
    selection: false
  });

  const filterTabItems = useMemo(() => {
    const getCallTypeCount = (type: string) =>
      data.filter(call => call.calltype === type).length;

    // Obtener tipos únicos de llamadas
    const uniqueCallTypes = [...new Set(data.map(call => call.calltype))];

    const items = [
      {
        label: 'All',
        value: 'all',
        onClick: () => {
          table.getColumn('calltype')?.setFilterValue('');
        },
        count: data.length
      }
    ];

    // Añadir un item por cada tipo de llamada único
    uniqueCallTypes.forEach(type => {
      items.push({
        label: type,
        value: type,
        onClick: () => {
          table.getColumn('calltype')?.setFilterValue(type);
        },
        count: getCallTypeCount(type)
      });
    });

    return items;
  }, [data, table]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleMinDurationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMinDurationInput(e.target.value);
  };

  const handleMaxDurationChange = (e: ChangeEvent<HTMLInputElement>) => {
    setMaxDurationInput(e.target.value);
  };

  const handleMinDurationBlur = () => {
    let newMin = parseInt(minDurationInput, 10);

    // Validaciones
    if (isNaN(newMin)) newMin = 0;
    if (newMin < 0) newMin = 0;
    if (newMin > durationRange[1]) newMin = durationRange[1];

    // Actualizar el slider y el input
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.set([newMin, durationRange[1]]);
    }
  };

  const handleMaxDurationBlur = () => {
    let newMax = parseInt(maxDurationInput, 10);

    // Validaciones
    if (isNaN(newMax)) newMax = maxDuration;
    if (newMax > maxDuration) newMax = maxDuration;
    if (newMax < durationRange[0]) newMax = durationRange[0];

    // Actualizar el slider y el input
    if (sliderInstanceRef.current) {
      sliderInstanceRef.current.set([durationRange[0], newMax]);
    }
  };

  const handleDurationKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Pierde el foco para activar el evento onBlur
    }
  };

  const handleRowClick = (row: Row<Call>) => {
    const original = row.original;

    // Solo llamar a la función callback
    if (onCallIdSelect) {
      onCallIdSelect(
        original.callid,
        original.campaignid,
        original.customerfullname,
        original.calldatetime + ' (Phone number: ' + original.phonenumber + ')',
        original.audiosource
      );
    }
  };

  const getRowClassName = (row: Row<Call>) => {
    const baseClasses = 'position-static';
    const isSelected = row.original.callid === selectedCallId;

    if (isSelected) {
      return `${baseClasses} bg-secondary-subtle fw-bold`;
    }

    return baseClasses;
  };

  return (
    <div>
      <div className="mb-2">
        <div className="d-flex flex-wrap gap-3 justify-content-between">
          <div className="d-flex gap-3">
            <SearchBox
              placeholder="Search calls"
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyPress}
              className="mt-0 mb-0"
            />
            <div className="d-flex align-items-center">
              <span className="fs-10 px-2 pe-1">Call Duration (sec.):</span>
              <input
                type="text"
                className="d-flex fs-10 fw-bold justify-content-end text-end"
                style={{
                  width: '30px',
                  border: 'none',
                  background: 'transparent',
                  outline: 'none'
                }}
                value={minDurationInput}
                onChange={handleMinDurationChange}
                onBlur={handleMinDurationBlur}
                onKeyDown={handleDurationKeyDown}
                aria-label="Minimum duration in seconds"
                title="Minimum duration in seconds"
              />
              <div
                className="noUi-primary-lighter noUi-handle-primary noUi-slider-slim noUi-handle-circle"
                ref={sliderRef}
                style={{
                  width: '130px',
                  margin: '12px 12px',
                  visibility: 'visible'
                }}
              ></div>
              <input
                type="text"
                className="fs-10 fw-bold text-start"
                style={{
                  width: '30px',
                  border: 'none',
                  background: 'transparent',
                  outline: 'none'
                }}
                value={maxDurationInput}
                onChange={handleMaxDurationChange}
                onBlur={handleMaxDurationBlur}
                onKeyDown={handleDurationKeyDown}
                aria-label="Maximun duration in seconds"
                title="Maximun duration in seconds"
              />
            </div>
          </div>
          <div className="d-flex align-items-center">
            <FilterTab tabItems={filterTabItems} className="mt-0" />
          </div>
        </div>
      </div>

      {isLoading ? ( // Mostrar spinner mientras se cargan los datos
        <div className="d-flex justify-content-center align-items-center p-5 pb-7">
          <HVASpinner />
        </div>
      ) : (
        <AdvanceTableProvider {...table}>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6">
            <AdvanceTable
              tableProps={{ size: 'sm', className: 'phoenix-table fs-9' }}
              rowClassName={getRowClassName}
              onRowClick={handleRowClick}
            />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>
      )}
    </div>
  );
};

export default CallList;
